import type { UserSubdivision } from 'src/entities/profile'
import { defineStore } from 'pinia'
import { canChooseSubdivision, hasPermission, PERMISSION } from 'src/entities/auth'
import { isSameSubdivision } from 'src/entities/profile'
import { getSubdivisions } from 'src/use-cases/profile'
import { useAuthStore } from 'stores/auth'
import { useCatalogStore } from 'stores/catalog'
import { computed, ref } from 'vue'

/**
 * Подразделение, данные которого смотрит пользователь. Своё приходит в токене;
 * с правом subdivisions:choose-* можно выбрать другое в профиле — тогда его id
 * уходит в X-Subdivision-Id (бэкенд учитывает заголовок только для GET, запись
 * всегда идёт в своё). Исключения: супер продавец (subdivisions:write-in-chosen)
 * работает в выбранном целиком — заказы, оплаты, выдача и возвраты идут в нём;
 * складской грузчик (issue:in-chosen-subdivision) — выбранное подразделение его
 * место выдачи, выбрать его нужно обязательно, и выдача идёт в нём. Выбор
 * хранится по subject, чтобы на общем устройстве у каждого пользователя был свой.
 */
export const useSubdivisionStore = defineStore(
  'subdivision',
  () => {
    const authStore = useAuthStore()

    const selectedBySubject = ref<Record<string, UserSubdivision>>({})
    const available = ref<UserSubdivision[] | null>(null)

    const canChoose = computed(() => canChooseSubdivision(authStore.permissions))
    /** Выбранное подразделение — место выдачи, а не просмотр. */
    const issuesInChosen = computed(
      () => canChoose.value && hasPermission(authStore.permissions, PERMISSION.ISSUE_IN_CHOSEN_SUBDIVISION),
    )
    /** Вся запись идёт в выбранное подразделение (супер продавец). */
    const writesInChosen = computed(
      () => canChoose.value && hasPermission(authStore.permissions, PERMISSION.SUBDIVISIONS_WRITE_IN_CHOSEN),
    )
    const ownId = computed(() => authStore.ownSubdivisionId || null)
    const own = computed<UserSubdivision | null>(
      () => available.value?.find(({ id }) => isSameSubdivision(id, ownId.value)) ?? null,
    )
    /** Выбранное в профиле; без права выбора не учитывается. */
    const selected = computed<UserSubdivision | null>(() =>
      canChoose.value ? (selectedBySubject.value[authStore.subject] ?? null) : null,
    )
    /** Выбрано не своё: заголовок нужен только в этом случае (месту выдачи — всегда). */
    const foreign = computed<UserSubdivision | null>(() =>
      selected.value && (issuesInChosen.value || !isSameSubdivision(selected.value.id, ownId.value))
        ? selected.value
        : null,
    )
    /** Подразделение, данные которого сейчас на экране. */
    const current = computed<UserSubdivision | null>(
      () => foreign.value ?? own.value ?? (canChoose.value ? null : available.value?.[0] ?? null),
    )
    /** Значение заголовка X-Subdivision-Id. */
    const headerValue = computed(() => foreign.value?.id ?? null)
    /**
     * Работает не в своём подразделении: запись идёт в своё, кроме выдачи на месте
     * выдачи и работы супер продавца.
     */
    const isAwayFromOwn = computed(
      () => !!ownId.value && !!foreign.value && !isSameSubdivision(foreign.value.id, ownId.value),
    )
    /**
     * Смотрит чужое подразделение при наличии своего: плашка «Просмотр», действия
     * на запись скрыты. Место выдачи складского грузчика просмотром не считается.
     */
    const isViewingForeign = computed(
      () => isAwayFromOwn.value && !issuesInChosen.value && !writesInChosen.value,
    )
    /** Супер продавец работает в чужом подразделении: плашка «Работаете в», запись идёт туда. */
    const isWorkingForeign = computed(() => isAwayFromOwn.value && writesInChosen.value)
    /**
     * Без выбора нельзя работать: своего нет (бэкенд не отдаст данные) или это
     * складской грузчик без места выдачи.
     */
    const needsSelection = computed(
      () => canChoose.value && (!ownId.value || issuesInChosen.value) && !selected.value,
    )

    function select(subdivision: UserSubdivision) {
      if (!issuesInChosen.value && isSameSubdivision(subdivision.id, ownId.value)) {
        clearSelected()
        return
      }
      if (selected.value?.id === subdivision.id) return

      selectedBySubject.value = {
        ...selectedBySubject.value,
        [authStore.subject]: subdivision,
      }
      // Категории (в т.ч. уценка) считаются по складам подразделения.
      useCatalogStore().$reset()
    }

    function clearSelected() {
      if (!(authStore.subject in selectedBySubject.value)) return

      const { [authStore.subject]: _, ...rest } = selectedBySubject.value
      selectedBySubject.value = rest
      useCatalogStore().$reset()
    }

    async function fetchAvailable(signal?: AbortSignal) {
      const list = await getSubdivisions.execute(signal)
      available.value = list

      if (!canChoose.value) return list

      const chosen = selected.value
      if (chosen && !list.some(({ id }) => id === chosen.id)) {
        clearSelected()
      }
      // Выбирать нужно, а вариант один — выбираем сами.
      if (needsSelection.value && list.length === 1) {
        select(list[0])
      }

      return list
    }

    return {
      selectedBySubject,
      available,
      canChoose,
      issuesInChosen,
      writesInChosen,
      ownId,
      own,
      selected,
      current,
      headerValue,
      isAwayFromOwn,
      isViewingForeign,
      isWorkingForeign,
      needsSelection,
      select,
      clearSelected,
      fetchAvailable,
    }
  },
  {
    persist: { pick: ['selectedBySubject'] },
  },
)
